import { memo, useId } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { SparklineProps } from "./types";

/**
 * Trend uz KPI broj — bez osi, mreže i legende. Jedna serija po kartici, a identitet
 * nosi natpis iznad, ne boja: zato zelena i crvena smiju stajati u susjednim karticama.
 *
 * Podnožje se veže na `dataMin`, ne na nulu — inače bi kod velikih iznosa linija
 * bila spljoštena uz gornji rub i trend se ne bi vidio.
 */
const Sparkline: React.FC<SparklineProps> = ({ points, hex, formatValue }) => {
  const gradientId = useId();

  // Veličinu zadaje roditelj — kartica je jedina koja zna koliko joj mjesta preostaje.
  if (points.length < 2) return <></>;

  return (
    <div className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={hex} stopOpacity={0.28} />
              <stop offset="100%" stopColor={hex} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Tooltip
            cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;

              const point = payload[0].payload as { label: string; value: number };

              return (
                <div className="rounded-sm bg-slate-800 px-2 py-1 text-xs text-white shadow-lg">
                  <div className="text-slate-300">{point.label}</div>
                  <div className="font-medium">{formatValue(point.value)}</div>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={hex}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            baseValue="dataMin"
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0, fill: hex }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default memo(Sparkline);
